import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { StatusCode } from "api/protocols";
import { UserLogin } from "api/rpc";
import { showError, showSuccess } from "redux/actions/ui";

import { useGuestPortalContext } from "./GuestPortalContext";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";

import "./portalLogin.scss";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { LockTeeTimeById } from "api/rpc/2022-09/guest/bookingEngine";
import { GetClientGuest } from "api/rpc/2022-09/guest/client";
import { GetGuestTeeSheetSettings } from "api/rpc/2022-09/guest/facility";
import { convertTime, formatDateFromString } from "helpers/Helpers";
import { useClientPortal } from "../Customer/ClientPortal/useClientPortal";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import { getFoodOrderingCartToken } from "../Customer/ClientPortal/FoodOrdering/Menu";
import { putCart } from "redux/actions/customer/cart";
import { ICart } from "redux/reducers/customer/models/cart";
import { UserType } from "redux/reducers/models/user";

interface ILoginState {
  email: string;
  password: string;
}

interface IParams {
  clientShortName: string;
}

export default function PortalLogin() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);
  // const cartStore = useAppSelector(store => store.customerCartStore);
  const { portalState, updatePortalState } = useGuestPortalContext();
  const { clientShortName } = useParams<IParams>();
  const clientPortal = window.location.href.includes(`/portal/${clientShortName}/login`);
  const HOME_URL = useClientPortal().getHomePageUrl();

  const [loginState, setLoginState] = useState<ILoginState>({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (!clientPortal && portalState.guest?.user_type === UserType.STANDARD_CUSTOMER) {
      history.replace("/tee-on/portal/overview");
    }
  }, [clientPortal, history, portalState.guest]);

  async function handleTeeOnPortalLogin(credentials: { email: string; password: string }) {
    const user = await handleLogin(credentials);

    if (!user) {
      return;
    }

    updatePortalState({ guest: user });
    dispatch(showSuccess(t("Logged in successfully"), 2000));

    history.push(`/tee-on/portal/overview`);
  }

  async function handleClientPortalLogin(credentials: { email: string; password: string }) {
    const user = await handleLogin(credentials);

    console.log("loginRes", user);

    if (!user) {
      return;
    }

    dispatch(clientPortalUpdate({ user: user }));

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const date = urlParams.get("date");
    const teeTimeId = urlParams.get("id");
    const facility = urlParams.get("facility");

    console.log(clientPortalStore);

    if (date && teeTimeId) {
      await handleNavigateToTeeTime(date, Number(teeTimeId), facility);
    } else if (
      clientPortalStore?.reservation &&
      clientPortalStore?.reservation?.segment &&
      clientPortalStore?.reservation?.location &&
      clientPortalStore?.reservation?.module &&
      clientPortalStore?.selectedFacility
    ) {
      history.push(
        `/portal/${clientShortName}/reservations/${clientPortalStore?.selectedFacility?.short_name}/booking?module_id=${clientPortalStore?.reservation?.module?.id}&start_time=${clientPortalStore?.reservation?.segment?.start_time}&date=${clientPortalStore?.reservation?.segment?.date}&location_id=${clientPortalStore?.reservation?.location?.id}`,
      );
    } else if (getFoodOrderingCartToken(clientPortalStore?.selectedFacility?.short_name)) {
      // If a food ordering cart is active, we update the active cart with the logged in user
      const getCartPromise: Promise<ICart> = dispatch(
        putCart(
          {
            customer_email: user.email,
            cart_token: getFoodOrderingCartToken(clientPortalStore?.selectedFacility?.short_name),
          },
          true,
        ),
      );

      const cart = await getCartPromise;

      if (cart !== undefined) {
        dispatch({
          type: "cart.update",
          payload: {
            isLoaded: true,
            cart: cart,
          },
        });
      }

      history.push(`/portal/${clientShortName}/food-ordering/menu/${clientPortalStore?.selectedFacility?.short_name}`);
    } else {
      // history.push(HOME_URL);
      history.goBack();
    }
  }

  async function handleLogin(credentials: { email: string; password: string }) {
    const loginRes = await UserLogin(credentials, true);

    if (loginRes.status !== StatusCode.OK) {
      dispatch(showError(t("Invalid Credentials, please try again")));
      return null;
    }

    return loginRes.data?.user;
  }

  async function handleNavigateToTeeTime(teeTimeDate: string, teeTimeId: number, facilityShortName: string) {
    let client = clientPortalStore?.client;
    let facility = clientPortalStore.selectedFacility;
    let foundTeeTime = clientPortalStore?.selectedTeeTime;
    let teeSheetSettings = clientPortalStore?.teeSheetSettings;

    if (!client) {
      const clientRes = await GetClientGuest({ short_name: clientShortName }, true);
      if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
        dispatch(showError(t("Error loading client")));
        return;
      }
      client = clientRes.data[0];
      dispatch(clientPortalUpdate({ client: client }));
    }

    if (!facility) {
      const foundFacility = client?.facilities?.find(facility => facility?.short_name === facilityShortName);
      if (foundFacility) {
        facility = foundFacility;
        dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
      }
    }

    if (!teeSheetSettings) {
      const settingsRes = await GetGuestTeeSheetSettings({ facility_id: facility?.id }, true);
      if (settingsRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error getting tee sheet settings")));
        return;
      }
      teeSheetSettings = settingsRes.data;
      dispatch(clientPortalUpdate({ teeSheetSettings }));
    }

    const teeTimeParams = {
      date: teeTimeDate,
      id: teeTimeId,
      facility_id: facility?.id,
      extended: true,
      turn_tee_time: true,
    };

    const teeTimeRes = await GetTeeTimesCustomer(teeTimeParams, true);

    if (teeTimeRes.status === StatusCode.PRECONDITION_FAILED) {
      dispatch(showError(teeTimeRes.message));
      history.push(HOME_URL);
      return;
    }

    if (teeTimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting selected tee time")));
      return;
    }

    if (teeTimeRes?.data?.length === 0) {
      dispatch(showError(t("Tee time is unavailable, please choose another")));
      history.push(HOME_URL);
      return;
    }
    const formattedStartTime = convertTime(teeTimeRes.data[0]?.start_time);
    const formattedDate = formatDateFromString(teeTimeRes.data[0]?.date);
    foundTeeTime = { ...teeTimeRes.data[0], formattedDate, formattedStartTime };

    if (foundTeeTime?.id === teeTimeId) {
      //If not null - User has access to their own locked teetime
      if (foundTeeTime?.lock !== null) {
        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(foundTeeTime?.lock?.start_time) + "Z";

        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(foundTeeTime?.lock?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        dispatch(
          clientPortalUpdate({
            selectedTeeTimeLock: {
              lockoutDuration: lockoutDuration,
              lockStartTime: lockStartTime,
              expireTime: foundTeeTime?.lock?.expire_time,
            },
            selectedTeeTime: foundTeeTime,
          }),
        );

        history.push(`/portal/${clientShortName}/booking/id?=${teeTimeId}&date=${teeTimeDate}`);
      } else {
        const bookingLockRes = await LockTeeTimeById({ tee_time_id: foundTeeTime?.id }, true);

        if (bookingLockRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error locking tee time")));
          return;
        }

        //Locked out message
        const bookingLockMessage = "Tee time already locked by user";
        const bookingLockResMessage = bookingLockRes.data.message;

        if (bookingLockMessage === bookingLockResMessage) {
          dispatch(showError(t("Booking is locked by another user, please try again in a few minutes..")));
          return;
        }

        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(bookingLockRes?.data?.start_time) + "Z";
        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(bookingLockRes?.data?.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        dispatch(
          clientPortalUpdate({
            selectedTeeTimeLock: {
              lockoutDuration: lockoutDuration,
              lockStartTime: lockStartTime,
              expireTime: bookingLockRes?.data?.expire_time,
            },
            selectedTeeTime: foundTeeTime,
          }),
        );

        history.push(`/portal/${clientShortName}/booking/id?=${teeTimeId}&date=${teeTimeDate}`);
      }
    } else {
      dispatch(showError(t("Tee time is unavailable, please choose another")));
      history.push(HOME_URL);
      return;
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const field = event.target.name === "password" ? "password" : "email";
    const { value } = event.target;
    setLoginState(prevState => ({ ...prevState, [field]: value }));
  }

  function readCredentialsFromForm(form: HTMLFormElement): { email: string; password: string } {
    const data = new FormData(form);
    return {
      email: String(data.get("email") ?? "").trim(),
      password: String(data.get("password") ?? ""),
    };
  }

  async function handleSignInSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const credentials = readCredentialsFromForm(event.currentTarget);
    setLoginState(credentials);

    if (clientPortal) {
      await handleClientPortalLogin(credentials);
    } else {
      await handleTeeOnPortalLogin(credentials);
    }
  }

  function navigateToRegister() {
    if (clientPortal) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const date = urlParams.get("date");
      const teeTimeId = urlParams.get("id");
      const facility = urlParams.get("facility");
      if (date && teeTimeId && facility) {
        history.push(`/portal/${clientShortName}/register?id=${teeTimeId}&date=${date}&facility=${facility}`);
      } else {
        history.push(`/portal/${clientShortName}/register`);
      }
    } else {
      history.push(`/tee-on/portal/register`);
    }
  }

  function navigateToForgotPassword() {
    if (clientPortal) {
      window.open(`/portal/${clientShortName}/forgot`);
    } else {
      window.open(`/tee-on/portal/forgot`);
    }
  }

  return (
    <>
      <div className="tee-on-portal-login">
        <div className="tee-on-portal-login-header-container">
          <div className="tee-on-portal-login-header-container-centered">
            <div className="tee-on-portal-login-header-container-inner">
              <img
                src={
                  clientPortal && clientPortalStore?.client?.logo_source_url
                    ? clientPortalStore?.client?.logo_source_url
                    : "/public/images/Tee-On.png"
                }
                alt={t("Client logo")}
              />
            </div>
          </div>
        </div>

        <div className="tee-on-portal-login-container">
          <div className="tee-on-portal-login-container-inner">
            <form className="tee-on-portal-login-container-group" onSubmit={handleSignInSubmit} method="post">
              <div className="tee-on-portal-login-text-group">
                <p className="sign-in-text">{t("Sign In")}</p>
                <br />
                <p className="sign-in-info">
                  {clientPortal
                    ? "Please sign into an account to book a tee time"
                    : "Please sign into an account to see profile information."}
                </p>
              </div>

              <div className="tee-on-portal-login-input-group">
                <label className="tee-on-portal-login-input-label" htmlFor="email">
                  {t("Email or username")}
                </label>
                <Input
                  value={loginState.email}
                  onChange={handleInputChange}
                  type="text"
                  name="email"
                  labelHidden
                  id="email"
                  autoComplete="username"
                />
              </div>

              <div className="tee-on-portal-login-input-group">
                <label className="tee-on-portal-login-input-label" htmlFor="password">
                  {t("Password")}
                </label>
                <Input.Password
                  value={loginState.password}
                  onChange={handleInputChange}
                  name="password"
                  id="password"
                  autoComplete="current-password"
                />
              </div>

              <div className="tee-on-portal-login-forgot-password">
                <Button type="text" htmlType="button" onClick={navigateToForgotPassword}>
                  {t("Forgot Password?")}
                </Button>
              </div>

              <div>
                <Button type="primary" htmlType="submit" block className="tee-on-portal-login-button">
                  {t("SIGN IN")}
                </Button>
              </div>
            </form>
            <div>
              <Button type="default" htmlType="button" block onClick={navigateToRegister}>
                {t("REGISTER")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
